/**
 * Confirmation of a change the assistant has prepared but not made.
 *
 * The assistant reads text written by the salon's own customers, so a "yes" in
 * chat text proves nothing: a transcript could talk the model into it. The
 * server therefore commits a write only when the BROWSER hands back a signed
 * token after a real click:
 *
 *   1. The step ends with `status: "needs_confirmation"`, a `summary`, the
 *      `details` of exactly what will be written and a one-use `confirm_token`.
 *   2. The card shows summary and details. "Ja, so umsetzen" sends the message
 *      "Ja, bitte so umsetzen." and, in that one request, `approved_tokens`.
 *   3. "Nein, abbrechen" sends the sentence only. A typed message never carries
 *      a token, whatever its text says.
 *
 * Tokens live in memory with the tool step. This module holds the pure rules;
 * `assistantService` sends the tokens, the card component collects them.
 */
import type { AssistantToolResult, ConfirmationDetail } from '../types/assistant';

export const CONFIRM_REPLY = 'Ja, bitte so umsetzen.';
export const DECLINE_REPLY = 'Nein, bitte nicht ändern.';

/** Shown when the server sends `needs_confirmation` without a summary. */
export const CONFIRMATION_FALLBACK = 'Der Assistent möchte etwas ändern und wartet auf deine Bestätigung.';

/** The server accepts at most this many tokens in one request. */
export const MAX_APPROVED_TOKENS = 5;
/** More lines than this in one preview are not a preview anybody reads. */
const MAX_DETAILS = 30;

/**
 * Where the approving click leaves its tokens: on the user message it sends
 * (`metadata.custom`). The adapter reads them from the message that STARTS its
 * run, so the approval is bound to that one message and to nothing global.
 */
export const APPROVAL_METADATA_KEY = 'approvedTokens';

/**
 * `metadata.custom` key of a message that was RESTORED (reload, or back from
 * another page) and had a change waiting when it was stored. The preview and
 * its token are gone with the page, so the message cannot offer the click any
 * more; the thread says so and offers to show the preview again.
 */
export const AWAITING_CONFIRMATION_KEY = 'awaitingConfirmation';
/** What "Vorschau erneut anzeigen" sends: an ordinary typed-style message, never a token. */
export const REPREVIEW_REPLY = 'Bitte zeig mir die Vorschau noch einmal.';

interface ContentPart {
  readonly type: string;
  readonly result?: unknown;
}

/** One step of a message that waits for the owner's yes or no. */
export interface PendingConfirmation {
  summary: string;
  details: ConfirmationDetail[];
  /** Null when the server sent none (an older server): the step can still be shown. */
  token: string | null;
}

/** `details` as it came over the wire → only well-formed lines, as plain strings. */
export function parseConfirmationDetails(raw: unknown): ConfirmationDetail[] {
  if (!Array.isArray(raw)) return [];
  const details: ConfirmationDetail[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { label, value } = entry as Record<string, unknown>;
    if (typeof label !== 'string' || typeof value !== 'string') continue;
    if (label.trim() === '' && value.trim() === '') continue;
    details.push({ label: label.trim(), value });
    if (details.length === MAX_DETAILS) break;
  }
  return details;
}

/** The finished tool result of a part, when it has one of the expected shape. */
export function toolResultOf(part: ContentPart): AssistantToolResult | null {
  if (part.type !== 'tool-call') return null;
  const result = part.result;
  if (typeof result !== 'object' || result === null) return null;
  const { status, summary, details, confirmToken } = result as Record<string, unknown>;
  if (status !== 'ok' && status !== 'error' && status !== 'needs_confirmation') return null;

  const parsed: AssistantToolResult = { status };
  if (typeof summary === 'string' && summary.trim() !== '') parsed.summary = summary;
  const lines = parseConfirmationDetails(details);
  if (lines.length > 0) parsed.details = lines;
  if (typeof confirmToken === 'string' && confirmToken !== '') parsed.confirmToken = confirmToken;
  return parsed;
}

/** One entry per step of this message that waits for the owner's yes or no. */
export function pendingConfirmations(content: ReadonlyArray<ContentPart>): PendingConfirmation[] {
  const pending: PendingConfirmation[] = [];
  for (const part of content) {
    const result = toolResultOf(part);
    if (result?.status !== 'needs_confirmation') continue;
    pending.push({
      summary: result.summary ?? CONFIRMATION_FALLBACK,
      details: result.details ?? [],
      token: result.confirmToken ?? null,
    });
  }
  return pending;
}

/** The tokens of every step the click approves (they travel together, in one request). */
export function approvalTokens(pending: readonly PendingConfirmation[]): string[] {
  const tokens: string[] = [];
  for (const step of pending) {
    if (step.token !== null && !tokens.includes(step.token)) tokens.push(step.token);
  }
  return tokens.slice(0, MAX_APPROVED_TOKENS);
}

/**
 * The message the "Ja, so umsetzen" click sends: the fixed sentence plus the
 * tokens, attached to the message itself. Shaped for `thread.append`.
 */
export function buildApprovalMessage(tokens: readonly string[]) {
  return {
    role: 'user' as const,
    content: [{ type: 'text' as const, text: CONFIRM_REPLY }],
    metadata: { custom: { [APPROVAL_METADATA_KEY]: [...tokens] } },
  };
}

interface MessageLike {
  readonly role: string;
  readonly metadata?: { readonly custom?: Record<string, unknown> };
}

/**
 * Tokens a USER message carries. Only the click puts any there; a typed
 * message has none, whatever its text says. Malformed entries are dropped.
 */
export function approvalTokensOf(message: MessageLike | undefined): string[] {
  if (message?.role !== 'user') return [];
  const raw = message.metadata?.custom?.[APPROVAL_METADATA_KEY];
  if (!Array.isArray(raw)) return [];
  return raw.filter((token): token is string => typeof token === 'string' && token !== '');
}

/** Does this restored message have to say that its preview is gone? */
export function isRestoredPreview(custom: Record<string, unknown> | undefined): boolean {
  return custom?.[AWAITING_CONFIRMATION_KEY] === true;
}

/**
 * The card belongs under the newest message only, and only while nothing is
 * running: any later message — the answer to the card included — retires it.
 */
export function shouldOfferConfirmation(state: {
  isLast: boolean;
  isRunning: boolean;
  role: string;
}): boolean {
  return state.role === 'assistant' && state.isLast && !state.isRunning;
}
